// useProctoringSession — comprehensive proctoring for voice/audio interviews.
//
// Monitors:
//   Browser : tab switch, window minimize, fullscreen exit, focus loss
//   Camera  : no face, multiple faces, face left frame (via window.FaceDetector)
//   Audio   : background noise, external voices (via Web Audio API)
//   Device  : mobile/tablet UA (flagged at start, not violation)
//
// Returns:
//   violations[]     — full timeline with screenshots
//   integrityScore   — 0-100 (starts at 100, deductions per violation)
//   riskLevel        — "low" | "medium" | "high"
//   counters         — browserEvents, cameraEvents, audioEvents
//   proctorData()    — snapshot object to send to /agent/finish

import { useEffect, useRef, useState, useCallback } from "react";
import * as faceapi from "face-api.js";

const SEVERITY = {
  // High — strong evidence of cheating
  tab_switch:      "high",
  multi_face:      "high",
  no_face:         "high",
  external_voice:  "high",
  // Medium
  fullscreen_exit: "medium",
  window_minimize: "medium",
  focus_loss:      "medium",
  background_noise:"medium",
  left_frame:      "medium",
};

const DEDUCT = { high: 10, medium: 5, low: 3 };
const MAX_SCREENSHOTS = 20;   // cap to avoid huge payloads
const FACE_POLL_MS        = 3000;
const FACE_COOLDOWN       = 10000; // ms between same-type camera violations
const FACE_MISS_THRESHOLD = 3;     // consecutive misses before firing violation
const NOISE_INTERVAL = 8000;  // ms between noise violation fires

function computeRisk(score) {
  if (score >= 80) return "low";
  if (score >= 50) return "medium";
  return "high";
}

export function useProctoringSession({ active, videoRef }) {
  const [violations, setViolations]       = useState([]);
  const [integrityScore, setIntegrityScore] = useState(100);

  // Counters (not re-rendered every tick — kept in refs, synced to state via proctorData)
  const countersRef = useRef({
    tabSwitches:      0,
    windowMinimizes:  0,
    focusLosses:      0,
    fullscreenExits:  0,
    noFaceCount:      0,
    multiFaceCount:   0,
    leftFrameCount:   0,
    noiseCount:       0,
    externalVoiceCount: 0,
  });
  const scoreRef           = useRef(100);
  const violationsRef      = useRef([]);
  const screenshotCountRef = useRef(0);
  const lastCameraViolRef  = useRef({});  // type → timestamp of last fire
  const lastNoiseRef       = useRef(0);
  const faceDetectorRef    = useRef(null);
  const faceTimerRef       = useRef(null);
  const audioCtxRef        = useRef(null);
  const analyserRef        = useRef(null);
  const audioSrcRef        = useRef(null);
  const noiseTimerRef      = useRef(null);
  const streamRef          = useRef(null);

  // ── Screenshot capture ───────────────────────────────────────────────
  const captureSnapshot = useCallback(() => {
    if (screenshotCountRef.current >= MAX_SCREENSHOTS) return null;
    const video = videoRef?.current;
    if (!video || video.readyState < 2) return null;
    try {
      const canvas = document.createElement("canvas");
      canvas.width  = 160;
      canvas.height = 120;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, 160, 120);
      screenshotCountRef.current++;
      return canvas.toDataURL("image/jpeg", 0.6);
    } catch (_) {
      return null;
    }
  }, [videoRef]);

  // ── Log a violation ──────────────────────────────────────────────────
  const logViolation = useCallback((type, description) => {
    const severity  = SEVERITY[type] || "low";
    const deduction = DEDUCT[severity] || 3;
    const newScore  = Math.max(0, scoreRef.current - deduction);
    scoreRef.current = newScore;
    setIntegrityScore(newScore);

    const entry = {
      type,
      description,
      severity,
      timestamp:  new Date(),
      screenshot: captureSnapshot(),
    };
    violationsRef.current = [...violationsRef.current, entry];
    setViolations(v => [...v, entry]);

    // Update counter
    const c = countersRef.current;
    if (type === "tab_switch")       c.tabSwitches++;
    if (type === "window_minimize")  c.windowMinimizes++;
    if (type === "focus_loss")       c.focusLosses++;
    if (type === "fullscreen_exit")  c.fullscreenExits++;
    if (type === "no_face")          c.noFaceCount++;
    if (type === "multi_face")       c.multiFaceCount++;
    if (type === "left_frame")       c.leftFrameCount++;
    if (type === "background_noise") c.noiseCount++;
    if (type === "external_voice")   c.externalVoiceCount++;
  }, [captureSnapshot]);

  // ── Browser monitoring ───────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;

    const onHide = () => {
      if (document.hidden) {
        logViolation("tab_switch", "Candidate switched to another tab or minimized the browser window.");
      }
    };
    const onBlur = () => {
      logViolation("focus_loss", "Browser window lost focus — candidate may have switched to another application.");
    };
    const onFullscreen = () => {
      if (!document.fullscreenElement) {
        logViolation("fullscreen_exit", "Candidate exited fullscreen mode during the interview.");
      }
    };

    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFullscreen);

    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFullscreen);
    };
  }, [active, logViolation]);

  // ── Camera monitoring (face-api.js — works in all browsers) ────────
  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    async function startCamera() {
      try {
        if (!videoRef?.current) return;
        if (!videoRef.current.srcObject) {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
          streamRef.current = stream;
          videoRef.current.srcObject = stream;
          await new Promise(resolve => { videoRef.current.onloadedmetadata = resolve; });
          videoRef.current.play().catch(() => {});
        }

        // Load face-api.js TinyFaceDetector model
        if (!faceapi.nets.tinyFaceDetector.isLoaded) {
          await faceapi.nets.tinyFaceDetector.loadFromUri(`${process.env.PUBLIC_URL}/models`);
        }
        if (cancelled) return;

        let consecutiveMisses = 0;
        faceTimerRef.current = setInterval(async () => {
          const video = videoRef.current;
          if (!video || video.paused || video.readyState < 2) return;
          try {
            const detections = await faceapi.detectAllFaces(
              video,
              new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.3 })
            );
            const count = detections.length;
            const now   = Date.now();
            const sinceNoFace = now - (lastCameraViolRef.current.no_face   || 0);
            const sinceMulti  = now - (lastCameraViolRef.current.multi_face || 0);

            if (count === 0) {
              consecutiveMisses++;
              // Only fire after FACE_MISS_THRESHOLD consecutive misses to avoid
              // false positives from a single bad frame (lighting, angle, blink)
              if (consecutiveMisses >= FACE_MISS_THRESHOLD && sinceNoFace > FACE_COOLDOWN) {
                lastCameraViolRef.current.no_face = now;
                logViolation("no_face", "No face detected — candidate may have left the desk.");
              }
            } else {
              consecutiveMisses = 0; // reset on any successful detection
              if (count > 1 && sinceMulti > FACE_COOLDOWN) {
                lastCameraViolRef.current.multi_face = now;
                logViolation("multi_face", `${count} faces detected — another person may be present.`);
              }
            }
          } catch (_) {}
        }, FACE_POLL_MS);
      } catch (e) {
        console.warn("[proctor] camera start failed:", e.message);
      }
    }

    startCamera();

    return () => {
      cancelled = true;
      clearInterval(faceTimerRef.current);
    };
  }, [active, videoRef, logViolation]);

  // ── Audio monitoring (Web Audio API) ────────────────────────────────
  useEffect(() => {
    if (!active) return;

    async function startAudio() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const ctx    = new (window.AudioContext || window.webkitAudioContext)();
        const src    = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        src.connect(analyser);
        audioCtxRef.current  = ctx;
        analyserRef.current  = analyser;
        audioSrcRef.current  = src;

        const buf = new Float32Array(analyser.fftSize);

        noiseTimerRef.current = setInterval(() => {
          if (!analyserRef.current) return;
          analyserRef.current.getFloatTimeDomainData(buf);

          // RMS for overall level
          let sumSq = 0;
          for (let i = 0; i < buf.length; i++) sumSq += buf[i] * buf[i];
          const rms = Math.sqrt(sumSq / buf.length);

          // ZCR — high ZCR + high RMS = voice-like signal
          let zcr = 0;
          for (let i = 1; i < buf.length; i++) {
            if (buf[i - 1] * buf[i] < 0) zcr++;
          }
          const zcrRate = zcr / buf.length;

          const now = Date.now();
          if (rms > 0.1 && now - lastNoiseRef.current > NOISE_INTERVAL) {
            lastNoiseRef.current = now;
            if (zcrRate > 0.15) {
              // Voice-like: could be external speaker
              logViolation("external_voice", "External voice or second speaker detected in the audio feed.");
              countersRef.current.externalVoiceCount = Math.max(0, countersRef.current.externalVoiceCount - 1 + 1);
            } else {
              logViolation("background_noise", "Significant background noise detected. Please move to a quieter environment.");
            }
          }
        }, NOISE_INTERVAL);
      } catch (e) {
        console.warn("[proctor] audio monitor start failed:", e.message);
      }
    }

    startAudio();

    return () => {
      clearInterval(noiseTimerRef.current);
      try { audioCtxRef.current?.close(); } catch (_) {}
    };
  }, [active, logViolation]);

  // ── Cleanup on deactivate ───────────────────────────────────────────
  useEffect(() => {
    if (!active) {
      clearInterval(faceTimerRef.current);
      clearInterval(noiseTimerRef.current);
      try { audioCtxRef.current?.close(); } catch (_) {}
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    }
  }, [active]);

  // ── Snapshot of all proctoring data for submission ──────────────────
  const proctorData = useCallback(() => {
    const c = countersRef.current;
    return {
      integrityScore: scoreRef.current,
      riskLevel:      computeRisk(scoreRef.current),
      totalViolations: violationsRef.current.length,
      browserEvents: {
        tabSwitches:     c.tabSwitches,
        windowMinimizes: c.windowMinimizes,
        focusLosses:     c.focusLosses,
        fullscreenExits: c.fullscreenExits,
      },
      cameraEvents: {
        noFaceCount:    c.noFaceCount,
        multiFaceCount: c.multiFaceCount,
        leftFrameCount: c.leftFrameCount,
      },
      audioEvents: {
        noiseCount:         c.noiseCount,
        externalVoiceCount: c.externalVoiceCount,
      },
      violations: violationsRef.current.map(v => ({
        type:        v.type,
        description: v.description,
        severity:    v.severity,
        timestamp:   v.timestamp,
        // screenshots omitted — too large for the report payload
      })),
    };
  }, []);

  return {
    violations,
    integrityScore,
    riskLevel: computeRisk(integrityScore),
    proctorData,
    logViolation,
  };
}
